import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'ezbuild_token';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // localStorage holds the JWT for email/password users.
  // Google users authenticate via httpOnly cookie (no client-side token).
  const getToken = () => localStorage.getItem(TOKEN_KEY);
  const setToken = (t) => {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  };

  const checkAuth = useCallback(async () => {
    const token = getToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { headers, credentials: 'include' });
      if (!res.ok) throw new Error('not auth');
      const data = await res.json();
      setUser(data);
    } catch {
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const token = getToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { headers, credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setUser(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginEmail = async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Login failed');
    setToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const registerEmail = async (email, password, name) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });
    const data = await res.json();
    if (!res.ok) {
      const detail = data.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg).join(', ')
        : (detail || 'Registration failed');
      throw new Error(msg);
    }
    setToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/app';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    const token = getToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', headers, credentials: 'include' });
    } catch { /* ignore */ }
    setToken(null);
    setUser(null);
  };

  const authedFetch = async (path, options = {}) => {
    const token = getToken();
    const headers = { ...(options.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(`${API_URL}${path}`, { ...options, headers, credentials: 'include' });
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      loginEmail,
      registerEmail,
      loginGoogle,
      logout,
      authedFetch,
      refreshUser,
      setUserFromCallback: (u) => setUser(u),
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
